import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, useLocation } from 'react-router-dom';
import AnimatedRoutes from './components/AnimatedRoutes';
import { ThemeProvider } from './contexts/ThemeContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { AuthProvider } from './contexts/AuthContext';
import { Toaster } from 'react-hot-toast';
import ViewportManager from './components/ViewportManager';
import MainLayout from './components/layout/MainLayout';
import LoadingBar from './components/ui/LoadingBar';
import { fetchWithAuth, checkSessionValidity } from './utils/api';
import PracticeMaterials from "./pages/PracticeMaterials";
import MaterialsManagement from "./pages/MaterialsManagement";
import SessionExpiredModal from './pages/SessionExpired';
import UnavailableModal from './pages/Unavailable';

// Declare global window properties for modal triggers
declare global {
  interface Window {
    showSessionExpiredModal: () => void;
    showUnavailableModal: () => void;
  }
}

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false);
  const [unavailableOpen, setUnavailableOpen] = useState(false);
  const location = useLocation();

  // Simulate initial app loading
  useEffect(() => {
    // Add a slight delay to show loading state
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);
  
  // Set up event listeners for user activity tracking
  useEffect(() => {
    // Function to update active status
    const updateActiveStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        // Try multiple endpoint formats to ensure one works in production
        const endpoints = [
          '/api/auth/heartbeat/',
          '/auth/heartbeat/',
          '/heartbeat/'
        ];
        
        // Try each endpoint until one works
        let success = false;
        for (const endpoint of endpoints) {
          try {
            await fetchWithAuth(endpoint, {
              method: 'POST'
            }, 0, true); // silent mode
            success = true;
            break;
          } catch (error) {
            console.log(`Heartbeat failed for ${endpoint}:`, error);
            // Continue trying other endpoints
          }
        }
        
        if (!success) {
          console.warn('All heartbeat endpoints failed');
        }
      } catch (error) {
        console.error('Error updating active status:', error);
      }
    };
    
    // Handle window focus
    const handleFocus = () => {
      updateActiveStatus();
    };
    
    // Handle before unload to mark user as inactive when closing tab/browser
    const handleBeforeUnload = () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      // Use synchronous approach for beforeunload - try multiple endpoints
      const logoutUrls = [
        '/logout-api/',
        '/api/logout-api/',
        '/auth/logout/'
      ];
      
      // We'll try the first URL with sendBeacon, which is all we can do in beforeunload
      try {
        // Create a proper payload with the token in the authorization header
        const headers = new Headers();
        headers.append('Authorization', `Token ${token}`);
        headers.append('Content-Type', 'application/json');
        
        const blob = new Blob([JSON.stringify({ token })], { type: 'application/json' });
        
        // Try to send the beacon with the proper headers
        const success = navigator.sendBeacon(logoutUrls[0], blob);
        
        if (!success) {
          // Try alternative endpoints if the first one fails
          for (let i = 1; i < logoutUrls.length; i++) {
            const altSuccess = navigator.sendBeacon(logoutUrls[i], blob);
            if (altSuccess) break;
          }
        }
      } catch (e) {
        console.error('Error sending logout beacon:', e);
        
        // Basic fallback - attempt to send a synchronous XHR request
        try {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', logoutUrls[0], false); // false = synchronous
          xhr.setRequestHeader('Authorization', `Token ${token}`);
          xhr.setRequestHeader('Content-Type', 'application/json');
          xhr.send(JSON.stringify({ token }));
        } catch (e) {
          console.error('Error with synchronous XHR fallback:', e);
        }
      }
    };
    
    // Add event listeners
    window.addEventListener('focus', handleFocus);
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // Clean up event listeners
    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Session polling for inactivity/forced logout
  useEffect(() => {
    const pollSession = async () => {
      await checkSessionValidity(true);
    };
    const interval = setInterval(pollSession, 60000); // 60 seconds (was 90 seconds)
    return () => clearInterval(interval);
  }, []);

  // Check session on route change
  useEffect(() => {
    checkSessionValidity(true);
  }, [location.pathname]);

  // Patch global error/session handling to show modals
  useEffect(() => {
    // Patch window to allow global modal triggers
    window.showSessionExpiredModal = () => setSessionExpiredOpen(true);
    window.showUnavailableModal = () => setUnavailableOpen(true);
  }, []);

  // Only show MainLayout if not on error pages (legacy, but keep for fallback)
  const isErrorPage = false; // Always false, modals now handle errors

  return (
    <ThemeProvider>
      <NotificationProvider>
        <AuthProvider>
          {/* Router removed, now provided at top level */}
          <ViewportManager />
          <LoadingBar isLoading={isLoading} />
          <Toaster 
            position="top-center" 
            toastOptions={{
              style: {
                background: '#333',
                color: '#fff',
              },
              success: {
                iconTheme: {
                  primary: '#10B981',
                  secondary: '#FFFFFF',
                },
              },
              error: {
                iconTheme: {
                  primary: '#EF4444',
                  secondary: '#FFFFFF',
                },
              }
            }}
          />
          <MainLayout><AnimatedRoutes /></MainLayout>
          <SessionExpiredModal open={sessionExpiredOpen} onClose={() => setSessionExpiredOpen(false)} />
          <UnavailableModal open={unavailableOpen} onClose={() => setUnavailableOpen(false)} />
        </AuthProvider>
      </NotificationProvider>
    </ThemeProvider>
  );
}

export default App;
